import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { MaskedLines, Reveal } from "../components/motion";
import { articles } from "../data/content";
import { usePageTitle } from "../lib/title";

export default function Education() {
  usePageTitle("Benefits Education & Articles");
  const [featured, ...rest] = articles;

  return (
    <div data-testid="education-page">
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-36 lg:px-10 lg:pt-44" data-testid="education-hero">
        <h1 className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="education-kicker">
          Health Insurance &amp; Employee Benefits Articles
        </h1>
        <MaskedLines
          as="p"
          className="mt-8 max-w-4xl font-serif text-5xl font-light leading-[0.95] tracking-tighter text-forest sm:text-6xl lg:text-7xl"
          lines={["Learn the system.", "Then make it work for you."]}
        />
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">
            Plain-language guides on health benefits for individuals, families, and Texas employers.
            No jargon, no sales pitch — just understanding.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32" data-testid="education-grid">
        <Reveal>
          <Link
            to={`/education/${featured.slug}`}
            data-testid={`article-card-${featured.slug}`}
            className="group grid gap-8 border border-border bg-sand/50 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60 lg:grid-cols-12 lg:p-12"
          >
            <div className="lg:col-span-8">
              <div className="flex items-center gap-4">
                <BookOpen className="h-4 w-4 text-gold" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">
                  Featured — {featured.category} — {featured.readTime}
                </span>
              </div>
              <h2 className="mt-6 font-serif text-3xl font-medium leading-snug tracking-tight text-forest transition-colors group-hover:text-terra sm:text-4xl">
                {featured.title}
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink/65">{featured.excerpt}</p>
            </div>
            <div className="flex items-end justify-end lg:col-span-4">
              <ArrowUpRight className="h-8 w-8 text-forest/30 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold" />
            </div>
          </Link>
        </Reveal>

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {rest.map((a, i) => (
            <Reveal key={a.slug} delay={0.08 * i}>
              <Link
                to={`/education/${a.slug}`}
                data-testid={`article-card-${a.slug}`}
                className="group flex h-full flex-col justify-between border border-border p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60 hover:bg-sand/50"
              >
                <div>
                  <div className="flex items-center gap-4">
                    <BookOpen className="h-4 w-4 text-gold" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">
                      {a.category} — {a.readTime}
                    </span>
                  </div>
                  <h3 className="mt-5 font-serif text-2xl font-medium leading-snug tracking-tight text-forest transition-colors group-hover:text-terra">
                    {a.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-ink/65">{a.excerpt}</p>
                </div>
                <ArrowUpRight className="mt-8 h-5 w-5 text-forest/40 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
